import React, {useState, useRef, useEffect} from "react";
import {Row, Col, Tooltip} from "antd";
import {InfoCircleOutlined} from "@ant-design/icons";
import {useObserver} from "mobx-react-lite";
import {useTranslation} from "react-i18next";
import {useStore} from "../../../../../hooks/useStore";
import {useFetch} from "../../../../../hooks/useFetch";
import useIsFocused from "../../../../../hooks/useIsFocused";
import {TimeRangeFilter} from "../../../customerHomePage/orderStats/timeRangeFilter/TimeRangeFilter";
import {SubCompanyFilter} from "../../../../_defaultComponents/FilterComponents";
import {PackageMetricsGrid} from "../PackageMetricsGrid/PackageMetricsGrid";
import {removeNullEntries} from "../../../../../utils/helpers";
import moment from "moment";
import {TWENTY_SECONDS_IN_MS} from "constants/constants";

export const PackageStatisticsTab = () => {
    const stores = useStore();
    const {t} = useTranslation(["timeRangeOptionsStatistics", "common"]);
    const isFocused = useIsFocused();

    // State for filters
    const [startDate, setStartDate] = useState(moment().format("YYYY-MM-DD"));
    const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
    const [dateType, setDateType] = useState("daily");
    const selectedSubCompanyId = stores?.meStore?.selectedSubCompanyId;
    const [subCompany, setSubCompany] = useState(selectedSubCompanyId || "all");
    const [dependencyCondition, setDependencyCondition] = useState(true);

    // Recall mechanism for data refresh
    const [recall, setRecall] = useState(true);
    const recallRef = useRef(recall);
    recallRef.current = recall;

    const currentCompanyId = stores?.meStore?.auth?.companies?.[0];
    const hasMultipleSubcompanies = stores?.meStore?.auth?.subcompanies?.length > 1;

    // Create subcompanies array for API calls
    const selectedSubCompanies = React.useMemo(() => {
        return subCompany === "all" ? [] : [subCompany];
    }, [subCompany]);

    const [statisticsLoading, statisticsData] = useFetch({
        fetchFn: stores.statisticStore.getHistoricStatistics.bind(
            null,
            removeNullEntries({
                period: dateType,
                startDate: startDate,
                endDate: endDate,
                subcompanies: selectedSubCompanies.join("|")
            })
        ),
        dependencies: [dateType, startDate, endDate, selectedSubCompanies, recall],
        initialCall: true,
        condition: dependencyCondition,
        defaultDataValue: {}
    });

    // Fetch duration statistics for delivery time breakdowns
    const [durationLoading, durationData] = useFetch({
        fetchFn: stores.statisticStore.getDurationStatistics.bind(
            null,
            removeNullEntries({
                period: dateType,
                startDate: startDate,
                endDate: endDate,
                minute: 45
            })
        ),
        dependencies: [dateType, startDate, endDate, subCompany, recall],
        initialCall: true,
        condition: dependencyCondition,
        defaultDataValue: {}
    });

    // Fetch merged order statistics for merge rate
    const [mergedLoading, mergedData] = useFetch({
        fetchFn: stores.statisticStore.getMergedOrderStatistics.bind(
            null,
            removeNullEntries({
                period: dateType,
                startDate: startDate,
                endDate: endDate
            })
        ),
        dependencies: [dateType, startDate, endDate, subCompany, recall],
        initialCall: true,
        condition: dependencyCondition,
        defaultDataValue: {}
    });

    const selectFieldOnChange = value => {
        setDependencyCondition(true);
        if (value === "daily") {
            setStartDate(moment().format("YYYY-MM-DD"));
            setEndDate(moment().format("YYYY-MM-DD"));
        } else if (value === "weekly") {
            setStartDate(
                moment()
                    .subtract(1, "week")
                    .format("YYYY-MM-DD")
            );
            setEndDate(moment().format("YYYY-MM-DD"));
        } else if (value === "monthly") {
            setStartDate(
                moment()
                    .subtract(1, "month")
                    .format("YYYY-MM-DD")
            );
            setEndDate(moment().format("YYYY-MM-DD"));
        } else if (value === "") {
            setStartDate("");
            setEndDate("");
        } else if (value === "choose") {
            setStartDate("");
            setEndDate("");
            setDependencyCondition(false);
        }
        setDateType(value);
    };

    const rangePickerOnChange = formatString => {
        setDependencyCondition(true);
        setStartDate(formatString[0]);
        setEndDate(formatString[1]);
    };

    // Combine all data for metrics
    const combinedData = {
        ...statisticsData,
        ...durationData,
        ...mergedData
    };

    const loading = statisticsLoading || durationLoading || mergedLoading;

    useEffect(() => {
        if (isFocused) {
            const timeout = setTimeout(() => {
                setRecall(!recallRef.current);
            }, TWENTY_SECONDS_IN_MS);

            return () => clearTimeout(timeout);
        }
    }, [recall, isFocused]);

    useEffect(() => {
        setSubCompany(selectedSubCompanyId);
    }, [selectedSubCompanyId]);

    return useObserver(() => (
        <div style={{padding: "20px 0"}}>
            {/* Filters Row */}
            <Row gutter={[20, 20]} style={{marginBottom: "30px"}}>
                <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "6px"
                        }}
                    >
                        <Tooltip
                            title="Günlük veriler, her gün sabah 06:00'dan ertesi gün sabah 06:00'a kadar olan 24 saatlik zaman dilimini kapsar."
                            placement="top"
                        >
                            <InfoCircleOutlined style={{color: "#000", fontSize: "12px"}} />
                        </Tooltip>
                    </div>
                    <TimeRangeFilter
                        dateType={dateType}
                        selectFieldOnChange={selectFieldOnChange}
                        rangePickerOnChange={rangePickerOnChange}
                        isOrdersPage={true}
                        showInfoTooltip={false}
                    />
                </Col>
                {hasMultipleSubcompanies && (
                    <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                        <SubCompanyFilter
                            showAllOption={true}
                            allKey="all"
                            value={subCompany}
                            onChange={setSubCompany}
                            companies={currentCompanyId ? [currentCompanyId] : []}
                            disabled={!currentCompanyId}
                        />
                    </Col>
                )}
            </Row>

            {/* Metrics Grid */}
            <PackageMetricsGrid data={combinedData} loading={loading} />
        </div>
    ));
};
